/**
 * The closed-dates (holidays) list on Booking settings (owner decision 10 of
 * docs/plans/crm-agent-scheduler-mode-plan.md; WP-10). Every booking grid,
 * the patient's /book page and the scheduling assistant's union calendar,
 * skips these tenant-local dates. The list is EMPTY until an admin adds dates:
 * "Add US federal holidays" only adds them to the unsaved draft, and the page's
 * Save stores them (nothing is filled in on its own).
 *
 * Controlled: the parent owns the list and saves it with the other booking
 * settings.
 */
import { useState } from 'react';
import { CalendarPlus, Plus, X } from 'lucide-react';
import { fieldCls } from '../filters';
import { holidayName, withFederalHolidays } from './schedulerLogic';

/** Today's date in the tenant's timezone, 'YYYY-MM-DD'. */
export function tenantToday(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  }
}

/** 'Thu, Nov 26, 2026' for a 'YYYY-MM-DD' key (a calendar date, no timezone math). */
function dateLabel(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

export default function ClosedDatesEditor({ value, onChange, timezone, disabled }: {
  value: string[];
  onChange: (next: string[]) => void;
  timezone: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [added, setAdded] = useState<string | null>(null);
  const today = tenantToday(timezone);
  const dates = [...value].sort();

  const addOne = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft) || value.includes(draft)) { setDraft(''); return; }
    onChange([...value, draft].sort());
    setDraft('');
    setAdded(null);
  };

  const addHolidays = () => {
    const r = withFederalHolidays(value, today);
    onChange(r.dates);
    setAdded(r.added.length
      ? `Added ${r.added.length} holiday${r.added.length === 1 ? '' : 's'}. Remove any day you stay open, then save.`
      : 'Every upcoming federal holiday is already on the list.');
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor="closed-date-add" className="block text-xs font-medium text-gray-600 dark:text-gray-300">Add a closed date</label>
          <input
            id="closed-date-add"
            type="date"
            value={draft}
            min={today}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOne(); } }}
            className={`${fieldCls} mt-0.5 w-44 tabular-nums`}
          />
        </div>
        <button
          type="button"
          onClick={addOne}
          disabled={disabled || !draft}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
        <button
          type="button"
          onClick={addHolidays}
          disabled={disabled}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
        >
          <CalendarPlus className="h-4 w-4" /> Add US federal holidays
        </button>
      </div>
      {added && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400" role="status">{added}</p>}

      {dates.length === 0 ? (
        <p className="mt-2.5 rounded-lg border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
          No closed dates. Calls can be booked on every open weekday, holidays included.
        </p>
      ) : (
        <ul className="mt-2.5 flex flex-wrap gap-1.5" aria-label="Closed dates">
          {dates.map((d) => {
            const past = d < today;
            const name = holidayName(d);
            return (
              <li
                key={d}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${past
                  ? 'border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500'
                  : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'}`}
              >
                <span className="tabular-nums">{dateLabel(d)}</span>
                {name && <span className="text-gray-500 dark:text-gray-400">{name}</span>}
                <button
                  type="button"
                  onClick={() => { onChange(value.filter((x) => x !== d)); setAdded(null); }}
                  disabled={disabled}
                  aria-label={`Remove ${dateLabel(d)}`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-red-400"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
